// Como ela está: três perguntas, todo dia, na mesma hora.
//
// Pedido do dono em 20/09, depois de descrever o que via: "não lembra da
// gente", "dorme muito de dia", "aperta a mão, olha e desvia". Nenhuma dessas
// frases, sozinha, diz se está melhorando — e a equipe, que a vê por cinco
// minutos, não tem como saber. Quem sabe é quem passa o dia no quarto.
//
// Por isso a tela é de marcar, não de escrever: três perguntas, três botões
// grandes cada, e acabou. Quem registra está cansado, às vezes de madrugada,
// muitas vezes sem óculos — o mesmo motivo que redesenhou o monitor. O que se
// pede tem que caber em vinte segundos, ou não vai ser feito todo dia. E é o
// TODO DIA que faz o dado valer: a sequência é o exame.
//
// O desenho fica em `Corpo`, sem busca e sem estado, para a ferramenta
// `conferir-evolucao.mts` renderizar a tela cheia e eu OLHAR antes de publicar.
import { useCallback, useEffect, useState } from 'react';
import {
  PERGUNTAS_DO_DIA, ROTULO_RESPOSTA, RESPOSTAS, MARCA_RESPOSTA,
  quantosSim, sonoEmTexto, tendenciaDaEvolucao, formatarData, formatarDiaMes, hojeISO,
  type RespostaDoDia, type ChaveDaPergunta,
} from '@medicov1/shared';
import { chamar } from '../api';
import { Ditar } from '../componentes/Ditar';
import './evolucao.css';

export interface ObservacaoDTO {
  id: string;
  dia: string;
  obedeceu: RespostaDoDia;
  olhou: RespostaDoDia;
  comunicou: RespostaDoDia;
  sonoDiaHoras: number | null;
  sonoNoiteHoras: number | null;
  observacao: string | null;
  registradoEm: string;
  autor: string;
}

interface Lista {
  hoje: string;
  deHoje: ObservacaoDTO | null;
  observacoes: ObservacaoDTO[];
}

type Respostas = Partial<Record<ChaveDaPergunta, RespostaDoDia>>;

interface PropsCorpo {
  dia: string;
  aoTrocarDia: (dia: string) => void;
  hoje: string;
  respostas: Respostas;
  aoResponder: (chave: ChaveDaPergunta, resposta: RespostaDoDia) => void;
  sonoDia: string;
  aoTrocarSonoDia: (v: string) => void;
  sonoNoite: string;
  aoTrocarSonoNoite: (v: string) => void;
  observacao: string;
  aoTrocarObservacao: (v: string) => void;
  /** A observação que já existe para o dia escolhido — registrar de novo corrige. */
  jaRegistrada: ObservacaoDTO | null;
  anteriores: ObservacaoDTO[];
  podeRegistrar: boolean;
  enviando: boolean;
  erro: string;
  salvo: string;
  aoEnviar: () => void;
  aoVoltar: () => void;
}

/** Só desenho: sem busca, sem estado. É o que a prova visual renderiza. */
export function Corpo({
  dia, aoTrocarDia, hoje, respostas, aoResponder,
  sonoDia, aoTrocarSonoDia, sonoNoite, aoTrocarSonoNoite,
  observacao, aoTrocarObservacao, jaRegistrada, anteriores,
  podeRegistrar, enviando, erro, salvo, aoEnviar, aoVoltar,
}: PropsCorpo) {
  const completo = PERGUNTAS_DO_DIA.every((p) => respostas[p.chave]);
  const tendencia = tendenciaDaEvolucao(anteriores);

  return (
    <main className="ev">
      <header className="ev__topo">
        <div>
          <h1>Como ela está</h1>
          <p>Três perguntas, todo dia na mesma hora. É a sequência que mostra a evolução.</p>
        </div>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      {/* O dia vem preenchido com hoje. Trocar existe para quem esqueceu ontem
          — não para registrar o futuro, que o campo barra. */}
      <div className="ev__dia">
        <label htmlFor="ev-dia">Dia observado</label>
        <input id="ev-dia" type="date" value={dia} max={hoje}
          onChange={(e) => aoTrocarDia(e.target.value)} />
        <span>{dia === hoje ? 'hoje' : formatarData(dia)}</span>
      </div>

      {jaRegistrada && (
        <p className="ev__ja">
          Este dia já foi registrado por <b>{jaRegistrada.autor}</b>. Se marcar de
          novo, <b>corrige</b> — não cria um segundo registro.
        </p>
      )}

      {/* ------------------------------------------------------- as perguntas */}
      {PERGUNTAS_DO_DIA.map((p, i) => {
        const marcada = respostas[p.chave];
        return (
          <section className="ev__pergunta" key={p.chave}>
            <h2>
              <i className="ev__numero">{i + 1}</i>
              {p.pergunta}
            </h2>
            <p className="ev__como">{p.comoTestar}</p>

            <div className="ev__opcoes" role="group" aria-label={p.pergunta}>
              {RESPOSTAS.map((r) => (
                <button
                  type="button"
                  key={r}
                  className={`ev__opcao ev__opcao--${r}${marcada === r ? ' ev__opcao--on' : ''}`}
                  aria-pressed={marcada === r}
                  disabled={!podeRegistrar}
                  onClick={() => aoResponder(p.chave, r)}
                >
                  {ROTULO_RESPOSTA[r]}
                </button>
              ))}
            </div>

            {/* O critério da opção marcada aparece depois de marcar: confirma o
                que foi entendido, sem encher a tela com três explicações. */}
            {marcada && <p className="ev__criterio">{p.criterio[marcada]}</p>}
          </section>
        );
      })}

      {/* ------------------------------------------------------------- o sono */}
      <section className="ev__pergunta">
        <h2><i className="ev__numero">4</i>Quanto dormiu?</h2>
        <p className="ev__como">
          Aproximado, em horas — ninguém cronometra. Dormir de dia e ficar
          acordada à noite é achado por si só, e por isso são dois campos.
        </p>
        <div className="ev__sono">
          <div className="campo">
            <label htmlFor="ev-sono-dia">De dia</label>
            <input id="ev-sono-dia" type="number" inputMode="decimal" min={0} max={24} step={0.5}
              placeholder="horas" value={sonoDia} disabled={!podeRegistrar}
              onChange={(e) => aoTrocarSonoDia(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="ev-sono-noite">À noite</label>
            <input id="ev-sono-noite" type="number" inputMode="decimal" min={0} max={24} step={0.5}
              placeholder="horas" value={sonoNoite} disabled={!podeRegistrar}
              onChange={(e) => aoTrocarSonoNoite(e.target.value)} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- uma linha só */}
      <section className="ev__pergunta">
        <h2><i className="ev__numero">5</i>Chamou atenção alguma coisa?</h2>
        <p className="ev__como">
          Uma linha. O relato comprido continua sendo em <b>Anotar</b> — aqui é
          só o que resume o dia.
        </p>
        <div className="comDitado">
          <input type="text" maxLength={500} placeholder="pediu água; não abriu os olhos de manhã"
            value={observacao} disabled={!podeRegistrar}
            onChange={(e) => aoTrocarObservacao(e.target.value)} />
          <Ditar valor={observacao} aoMudar={aoTrocarObservacao} rotulo="ditar a observação" />
        </div>
      </section>

      {erro && <p className="aviso-erro">{erro}</p>}
      {salvo && <p className="ev__salvo">{salvo}</p>}

      {podeRegistrar ? (
        <div className="ev__acoes">
          <button className="botao ev__gravar" onClick={aoEnviar} disabled={enviando || !completo}>
            {enviando ? 'Salvando…' : jaRegistrada ? 'Corrigir o dia' : 'Salvar o dia'}
          </button>
          {!completo && <span className="ev__falta">Marque as três primeiras para salvar.</span>}
        </div>
      ) : (
        <p className="ev__falta">Você acompanha, mas não registra. Quem registra vê os botões aqui.</p>
      )}

      {/* ------------------------------------------------------ os dias antes */}
      {anteriores.length > 0 && (
        <section className="ev__historico">
          <h2>Os últimos dias</h2>
          {tendencia && <p className="ev__tendencia">{tendencia}</p>}

          <div className="ev__rolagem">
            <table className="ev__tabela">
              <thead>
                <tr>
                  <th>Dia</th>
                  {PERGUNTAS_DO_DIA.map((p) => <th key={p.chave} className="ev__col">{p.curto}</th>)}
                  <th className="ev__col">Sono</th>
                  <th>O que chamou atenção</th>
                </tr>
              </thead>
              <tbody>
                {anteriores.map((o) => (
                  <tr key={o.id}>
                    <td className="ev__quando">
                      <b>{formatarDiaMes(o.dia)}</b>
                      <small>{quantosSim(o)}/3</small>
                    </td>
                    {PERGUNTAS_DO_DIA.map((p) => (
                      <td key={p.chave} className={`ev__col ev__marca ev__marca--${o[p.chave]}`}
                        title={ROTULO_RESPOSTA[o[p.chave]]}>
                        {MARCA_RESPOSTA[o[p.chave]]}
                      </td>
                    ))}
                    <td className="ev__col ev__sonoCel">{sonoEmTexto(o) ?? '—'}</td>
                    <td className="ev__nota">{o.observacao ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="ev__legenda">
            <b>S</b> sim · <b>±</b> mais ou menos · <b>N</b> não.
            Isto é observação da família, não exame: mostra a linha, e quem lê a linha é a equipe.
          </p>
        </section>
      )}
    </main>
  );
}

interface Props {
  pacienteId: string;
  podeRegistrar: boolean;
  aoVoltar: () => void;
  /** Avisa o monitor que o dia de hoje foi registrado — o lembrete some. */
  aoGravar?: () => void;
}

export function Evolucao({ pacienteId, podeRegistrar, aoVoltar, aoGravar }: Props) {
  const [dia, setDia] = useState(hojeISO());
  const [lista, setLista] = useState<Lista | null>(null);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [sonoDia, setSonoDia] = useState('');
  const [sonoNoite, setSonoNoite] = useState('');
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvo, setSalvo] = useState('');

  const carregar = useCallback(async () => {
    try {
      setLista(await chamar<Lista>(`/observacoes?pacienteId=${pacienteId}&dias=30`));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui buscar os dias anteriores');
    }
  }, [pacienteId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const jaRegistrada = lista?.observacoes.find((o) => o.dia === dia) ?? null;

  // Trocar de dia traz o que já foi marcado naquele dia — senão a pessoa
  // corrigiria o dia inteiro sem ver o que já estava lá.
  useEffect(() => {
    if (!jaRegistrada) return;
    setRespostas({
      obedeceu: jaRegistrada.obedeceu, olhou: jaRegistrada.olhou, comunicou: jaRegistrada.comunicou,
    });
    setSonoDia(jaRegistrada.sonoDiaHoras?.toString() ?? '');
    setSonoNoite(jaRegistrada.sonoNoiteHoras?.toString() ?? '');
    setObservacao(jaRegistrada.observacao ?? '');
  }, [jaRegistrada]);

  const enviar = useCallback(async () => {
    const { obedeceu, olhou, comunicou } = respostas;
    if (!obedeceu || !olhou || !comunicou) return;

    setEnviando(true);
    setErro('');
    try {
      // Campo vazio é "não sei", não zero: mandar 0 diria que ela não dormiu
      // nada, que é outra informação.
      const horas = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
      await chamar('/observacoes', {
        metodo: 'POST',
        corpo: {
          pacienteId, dia, obedeceu, olhou, comunicou,
          sonoDiaHoras: horas(sonoDia), sonoNoiteHoras: horas(sonoNoite),
          observacao: observacao.trim() || null,
        },
      });
      setSalvo(`Dia ${formatarData(dia)} registrado. Amanhã, na mesma hora.`);
      await carregar();
      aoGravar?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui salvar');
    } finally {
      setEnviando(false);
    }
  }, [pacienteId, dia, respostas, sonoDia, sonoNoite, observacao, carregar, aoGravar]);

  return (
    <Corpo
      dia={dia}
      aoTrocarDia={(d) => { setDia(d); setSalvo(''); }}
      hoje={lista?.hoje ?? hojeISO()}
      respostas={respostas}
      aoResponder={(chave, r) => { setRespostas((atual) => ({ ...atual, [chave]: r })); setSalvo(''); }}
      sonoDia={sonoDia} aoTrocarSonoDia={setSonoDia}
      sonoNoite={sonoNoite} aoTrocarSonoNoite={setSonoNoite}
      observacao={observacao} aoTrocarObservacao={setObservacao}
      jaRegistrada={jaRegistrada}
      anteriores={lista?.observacoes ?? []}
      podeRegistrar={podeRegistrar}
      enviando={enviando} erro={erro} salvo={salvo}
      aoEnviar={() => void enviar()}
      aoVoltar={aoVoltar}
    />
  );
}
